import { useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { Link } from "react-router-dom";
import { FaPlus, FaEdit, FaTrash, FaUsers } from "react-icons/fa";

import Button from "../components/Button";
import Modal from "../components/Modal";
import FormInput from "../components/FormInput";

const GET_BOARDS = gql`
  query GetSalesFunnelBoards {
    salesFunnelBoards {
      id
      name
      description
      stages {
        id
        name
      }
      author {
        id
        name
        picture
      }
      teams {
        id
        name
      }
      createdAt
    }
  }
`;

const CREATE_BOARD = gql`
  mutation CreateSalesFunnelBoard($name: String!, $description: String, $teamIds: [ID!]) {
    createSalesFunnelBoard(name: $name, description: $description, teamIds: $teamIds) {
      success
      message
      id
    }
  }
`;

const DELETE_BOARD = gql`
  mutation DeleteSalesFunnelBoard($id: ID!) {
    deleteSalesFunnelBoard(id: $id) {
      success
      message
    }
  }
`;

export default function SalesFunnelBoardsPage() {
  const { data, loading, refetch } = useQuery(GET_BOARDS);
  const [createBoard] = useMutation(CREATE_BOARD);
  const [deleteBoard] = useMutation(DELETE_BOARD);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newBoardName, setNewBoardName] = useState("");
  const [newBoardDescription, setNewBoardDescription] = useState("");

  const handleCreateBoard = async () => {
    if (!newBoardName.trim()) return;

    try {
      await createBoard({
        variables: {
          name: newBoardName,
          description: newBoardDescription || null,
        },
      });
      setShowCreateModal(false);
      setNewBoardName("");
      setNewBoardDescription("");
      refetch();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const handleDeleteBoard = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this board? All items will be lost.")) {
      return;
    }

    try {
      await deleteBoard({ variables: { id } });
      refetch();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  return (
    <div className="w-full p-4 pt-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl text-title font-bold font-sans">Yood Boards</h1>
        <Button onClick={() => setShowCreateModal(true)}>
          <FaPlus className="inline mr-2" />
          New Board
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-10">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data?.salesFunnelBoards?.map((board: any) => (
            <div
              key={board.id}
              className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow"
            >
              <div className="flex justify-between items-start mb-2">
                <Link
                  to={`/funnel/${board.id}`}
                  className="text-lg font-semibold text-primary hover:underline"
                >
                  {board.name}
                </Link>
                <div className="flex space-x-2">
                  <Link
                    to={`/funnel/${board.id}/settings`}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FaEdit />
                  </Link>
                  <button
                    onClick={() => handleDeleteBoard(board.id)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>

              {board.description && (
                <p className="text-sm text-gray-500 mb-3">{board.description}</p>
              )}

              <div className="flex items-center text-sm text-gray-400">
                <span className="mr-4">{board.stages?.length || 0} stages</span>
                {board.teams && board.teams.length > 0 && (
                  <span className="flex items-center mr-4">
                    <FaUsers className="mr-1" />
                    {board.teams.length} team{board.teams.length > 1 ? "s" : ""}
                  </span>
                )}
                {board.author && (
                  <span className="flex items-center gap-1">
                    {board.author.picture ? (
                      <img src={board.author.picture} alt={board.author.name} className="w-4 h-4 rounded-full" />
                    ) : null}
                    <span>{board.author.name}</span>
                  </span>
                )}
              </div>
            </div>
          ))}

          {(!data?.salesFunnelBoards || data.salesFunnelBoards.length === 0) && (
            <div className="col-span-full text-center py-10 text-gray-500">
              No boards yet. Create your first board to get started!
            </div>
          )}
        </div>
      )}

      {showCreateModal && (
        <Modal
          title="Create New Board"
          closeFunction={() => setShowCreateModal(false)}
          autoHeight
        >
          <div className="space-y-4">
            <FormInput
              label="Board Name"
              value={newBoardName}
              onChange={(e) => setNewBoardName(e.target.value)}
              placeholder="e.g., Sales Pipeline Q1"
            />
            <FormInput
              label="Description (optional)"
              value={newBoardDescription}
              onChange={(e) => setNewBoardDescription(e.target.value)}
              placeholder="Brief description of this board"
            />
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outlined" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateBoard}>Create Board</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
